/**
 * Verification utility functions for biometric identity verification
 */

/**
 * Normalizes "require document back" values to strict boolean.
 * Defaults to `fallback` when value is undefined or unrecognized.
 *
 * @param {*} value - Raw value from API/UI config
 * @param {boolean} fallback - Default value when not explicitly set
 * @returns {boolean}
 */
export function resolveRequireDocumentBack(value, fallback = true) {
  if (value === undefined || value === null) {
    return fallback;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value === 1;
  }
  const normalized = String(value).trim().toLowerCase();
  if (!normalized) {
    return fallback;
  }
  if (['1', 'true', 'yes', 'on', 'required', 'enabled'].includes(normalized)) {
    return true;
  }
  if (['0', 'false', 'no', 'off', 'optional', 'disabled'].includes(normalized)) {
    return false;
  }
  return fallback;
}

/**
 * Extracts verification configuration from document and signer data
 * Priority: recipient placeholder > document-level > legacy flag
 * 
 * @param {Object} document - Document object with Placeholders, IdentityVerification, RequiresIdentityVerification
 * @param {Object} signer - Signer object with objectId
 * @returns {Object} Configuration object with enabled, threshold, requireDocument
 */
export function extractVerificationConfig(document, signer) {
  if (!document || !signer) {
    return {
      enabled: false,
      threshold: 85,
      requireDocument: true,
      requireDocumentBack: true
    };
  }

  // 1. Check recipient placeholder level (highest priority)
  const placeholders = Array.isArray(document.Placeholders) ? document.Placeholders : [];
  const placeholder = placeholders.find(
    (p) => p.signerObjId === signer.objectId || p.signerPtr?.objectId === signer.objectId
  );

  if (placeholder?.IdentityVerification?.enabled) {
    return {
      enabled: true,
      threshold: clampThreshold(placeholder.IdentityVerification.threshold, 85),
      requireDocument: true,
      requireDocumentBack: resolveRequireDocumentBack(
        placeholder.IdentityVerification.requireDocumentBack,
        true
      )
    };
  }

  // 2. Check document-level configuration
  if (document.IdentityVerification?.enabled) {
    return {
      enabled: true,
      threshold: clampThreshold(document.IdentityVerification.threshold, 85),
      requireDocument: true,
      requireDocumentBack: resolveRequireDocumentBack(
        document.IdentityVerification.requireDocumentBack,
        true
      )
    };
  }

  // 3. Check legacy flag
  if (document.RequiresIdentityVerification === true) {
    return {
      enabled: true,
      threshold: 85,
      requireDocument: true,
      requireDocumentBack: true
    };
  }

  // Default: verification not required
  return {
    enabled: false,
    threshold: 85,
    requireDocument: true,
    requireDocumentBack: true
  };
}

/**
 * Clamps threshold value to valid range [0, 100]
 * 
 * @param {number} value - Threshold value to clamp
 * @param {number} fallback - Fallback value if input is invalid
 * @returns {number} Clamped threshold value
 */
export function clampThreshold(value, fallback = 85) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  return Math.max(0, Math.min(100, parsed));
}

/**
 * Logs verification error with context for debugging
 * 
 * @param {Error} error - Error object
 * @param {Object} context - Context object with step, documentId, signer, etc.
 */
export function logVerificationError(error, context = {}) {
  console.error('Verification Error:', {
    error: error?.message || String(error),
    code: error?.code,
    stack: error?.stack,
    context: {
      step: context.step,
      documentId: context.documentId,
      signerEmail: context.signer?.email,
      timestamp: Date.now()
    }
  });
}

/**
 * Maps AWS Rekognition error codes to user-friendly messages
 * 
 * @param {Error} error - Error object from Rekognition
 * @returns {string} User-friendly error message
 */
export function mapRekognitionError(error) {
  const errorCode = error?.code || error?.name || '';
  
  const errorMap = {
    'InvalidImageFormatException': 'Invalid image format. Please use JPEG or PNG.',
    'ImageTooLargeException': 'Image is too large. Please use a smaller image.',
    'InvalidParameterException': 'Invalid verification parameters. Please try again.',
    'ProvisionedThroughputExceededException': 'Service is busy. Please try again in a moment.',
    'ThrottlingException': 'Too many requests. Please wait and try again.',
    'AccessDeniedException': 'Access denied. Please check your permissions.',
    'ResourceNotFoundException': 'Resource not found. Please try again.'
  };

  return errorMap[errorCode] || 'Verification failed. Please try again.';
}

/**
 * Validates if a file type is a supported image format
 * 
 * @param {string} mimeType - MIME type of the file
 * @param {string} fileName - File name (optional, for extension check)
 * @returns {boolean} True if format is supported
 */
export function isValidImageFormat(mimeType, fileName = '') {
  const validMimeTypes = ['image/png', 'image/jpeg', 'image/jpg'];
  const normalizedMimeType = String(mimeType || '').toLowerCase();
  
  if (validMimeTypes.includes(normalizedMimeType)) {
    return true;
  }

  // Fallback: check file extension
  const extension = String(fileName || '')
    .split('.')
    .pop()
    ?.toLowerCase();
  
  return ['png', 'jpg', 'jpeg'].includes(extension);
}

/**
 * Creates ImageData object from file or data URL
 * 
 * @param {Object} params - Parameters
 * @param {string} params.dataUrl - Data URL with base64 image
 * @param {string} params.name - File name
 * @param {string} params.type - MIME type
 * @param {number} params.size - File size in bytes
 * @returns {Object} ImageData object
 */
export function createImageData({ dataUrl, name, type, size }) {
  const base64Match = dataUrl?.match(/^data:([^;]+);base64,(.+)$/);
  
  return {
    name: name || 'image.jpg',
    type: type || 'image/jpeg',
    size: size || 0,
    dataUrl: dataUrl || '',
    base64: base64Match ? base64Match[2] : '',
    capturedAt: Date.now()
  };
}
